import { useEffect, useMemo, useState, type ReactNode } from 'react'
import type { SiteContent } from '@/types/Contenu'
import { ContentContext, type ContentContextValue, type EtatContenu } from './contentStore'
import { DEFAULT_CONTENT } from './defaultContent'
import { repository } from './repository'

const messageErreur = (e: unknown) => (e instanceof Error ? e.message : 'Impossible de charger le contenu.')

const remplacerOuAjouter = <T extends { id: string }>(list: T[], item: T, auDebut: boolean) =>
  list.some((x) => x.id === item.id)
    ? list.map((x) => (x.id === item.id ? item : x))
    : auDebut
      ? [item, ...list]
      : [...list, item]

// Le contenu s'affiche immédiatement (cache, ou contenu du build au tout premier passage),
// puis il est remplacé par la version à jour dès qu'elle arrive.
export function ContentProvider({ children }: { children: ReactNode }) {
  const [contenuInitial] = useState(() => repository.contenuImmediat())
  // Sans cache, on n'affiche pas les prédications d'exemple du build : leurs adresses n'existent pas.
  const [content, setContent] = useState<SiteContent>(() => contenuInitial ?? { ...DEFAULT_CONTENT, predications: [] })
  const [contenuProvisoire, setContenuProvisoire] = useState(contenuInitial === null)
  const [etatContenu, setEtatContenu] = useState<EtatContenu>('chargement')
  const [erreurContenu, setErreurContenu] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    repository.getContent().then(
      (c) => {
        if (cancelled) return
        setContent(c)
        setContenuProvisoire(false)
        setErreurContenu(null)
        setEtatContenu('pret')
      },
      (e) => {
        if (cancelled) return
        setErreurContenu(messageErreur(e))
        setEtatContenu('erreur')
      },
    )
    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo<ContentContextValue>(() => {
    const appliquer = (maj: (c: SiteContent) => SiteContent) => setContent(maj)

    return {
      ...content,
      etatContenu,
      erreurContenu,
      contenuProvisoire,
      rechargerContenu: async () => {
        setEtatContenu('chargement')
        try {
          setContent(await repository.getContent())
          setContenuProvisoire(false)
          setErreurContenu(null)
          setEtatContenu('pret')
        } catch (e) {
          setErreurContenu(messageErreur(e))
          setEtatContenu('erreur')
        }
      },
      savePredication: async (p) => {
        const enregistree = await repository.savePredication(p)
        appliquer((c) => ({ ...c, predications: remplacerOuAjouter(c.predications, enregistree, true) }))
        return enregistree
      },
      deletePredication: async (id) => {
        await repository.deletePredication(id)
        appliquer((c) => ({ ...c, predications: c.predications.filter((p) => p.id !== id) }))
      },
      saveMembre: async (m) => {
        const enregistre = await repository.saveMembre(m)
        appliquer((c) => ({ ...c, equipe: remplacerOuAjouter(c.equipe, enregistre, false) }))
        return enregistre
      },
      deleteMembre: async (id) => {
        await repository.deleteMembre(id)
        appliquer((c) => ({ ...c, equipe: c.equipe.filter((m) => m.id !== id) }))
      },
      moveMembre: async (id, direction) => {
        const ids = content.equipe.map((m) => m.id)
        const from = ids.indexOf(id)
        const to = from + direction
        if (from < 0 || to < 0 || to >= ids.length) return
        ;[ids[from], ids[to]] = [ids[to], ids[from]]
        await repository.reorderEquipe(ids)
        appliquer((c) => ({
          ...c,
          equipe: ids.map((i) => c.equipe.find((m) => m.id === i)).filter((m) => m !== undefined),
        }))
      },
      saveMotDuPasteur: async (mot) => {
        const motDuPasteur = await repository.saveMotDuPasteur(mot)
        appliquer((c) => ({ ...c, motDuPasteur }))
      },
      saveDons: async (d) => {
        const dons = await repository.saveDons(d)
        appliquer((c) => ({ ...c, dons }))
      },
      saveContact: async (ct) => {
        const contact = await repository.saveContact(ct)
        appliquer((c) => ({ ...c, contact }))
      },
      reset: async () => setContent(await repository.reset()),
    }
  }, [content, etatContenu, erreurContenu, contenuProvisoire])

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>
}
